<script setup>
import { computed, h } from "vue";

import clsx from "clsx";
import { RouterLink, useRouter } from "vue-router";

import Reputation from "@/components/Icons/Reputation.vue";
import Verified from "@/components/Icons/Verified.vue";
import { getReputationTooltip } from "@/utils/reputationTooltip.js";
import { BaseURL } from "@/global/baseUrls.js";
import orgAvatar from '@/assets/images/newtheme/avatar.png'

const props = defineProps({
  src: {
    type: String,
    default: "",
  },
  href: {
    type: String,
    default: "",
  },
  storageId: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "",
  },
  classes: {
    type: String,
    default: "",
  },
  verified: {
    type: Boolean,
    default: false,
  },
  width: {
    type: Number,
    default: 16,
  },
  height: {
    type: Number,
    default: 16,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  isOrg: {
    type: Boolean,
    default: false,
  },
  // Add rating and isCurrentUser props for tooltip
  rating: {
    type: Number,
    default: null,
  },
  isCurrentUser: {
    type: Boolean,
    default: false,
  },
  civicScore: {
    type: Number,
    default: 0
  },
});


const getSrc = computed(() => {
  if (props.src) {
    return props.src;
  }
  if (props.storageId) {
    return BaseURL + props.storageId;
  }
  if (props.isOrg) {
    return "/images/orgava.svg";
    // return "/src/assets/images/newtheme/avatar.png";
  }
  return "/images/DefaultAvatar.svg";
});

// Compute tooltip text if rating is provided
const tooltipText = computed(() => {
  if (props.rating !== null && props.rating !== undefined) {
    return getReputationTooltip(props.rating, props.isCurrentUser);
  }
  return "";
});
const hasRating = computed(() => props.rating !== null && props.rating !== undefined && !Number.isNaN(Number(props.rating)));

//select civic color
const ringColor = computed(() => {
  const score = Number(props.rating) || 0;
  if (score >= 40 && score <= 100) {
    return "#22c55e";  // green
  }

  if (score >= -39 && score <= 39) {
    // return "#facc15";  // yellow
    // return "rgb(34 194 94)";
    // return "var(--quick-link-icon-bg)";
    return "#facc15";
  }

  if (score >= -100 && score <= -40) {
    return "#ef4444";  // red
  }

  return "#facc15"; // default fallback
});

const ringStyle = computed(() => {
  if (props.isOrg || !hasRating.value) return {};
  // return { '--avatar-ring-background': `conic-gradient(${ringColor.value} 0%, ${ringColor.value} 100%)` };
  return { border: `2px solid ${ringColor.value}` };
});

// Ensure broken external images don't cascade errors or cause repeated reflows
const onImgError = (e) => {
  // const fallback = props.isOrg ? "/images/orgava.svg" : "/images/DefaultAvatar.svg";
  const fallback = props.isOrg ? orgAvatar : "/images/DefaultAvatar.svg";
  if (e && e.target) {
    // Prevent error loop if fallback also fails
    e.target.onerror = null;
    if (e.target.src !== fallback) {
      const originalSrc = e.target.src;
      e.target.src = fallback;

      // Only log unexpected errors in development, suppress known external CDN issues
      if (import.meta.env.DEV) {
        const isInstagramCdn = originalSrc.includes('cdninstagram.com') || originalSrc.includes('scontent-');
        const isKnownExternalDomain = originalSrc.includes('sfstandard.com') || originalSrc.includes('content.sfstandard.com');
        if (!isInstagramCdn && !isKnownExternalDomain) {
          console.debug(`[BaseAvatar] Unexpected image load failure: ${originalSrc}`);
        }
      }
    }
  }
};
</script>

<template>
  <div class="avatar-ring" 
    :style="ringStyle"
    :title="tooltipText || undefined"
    >
    <component @click.stop :is="props.href ? 'RouterLink' : 'div'" :to="props.href || undefined" class="avatar"
      :class="clsx({ 'border-none': isOrg }, size, classes)">
      <img class="avatar-img" :src="getSrc" alt="user's avatar" loading="lazy" decoding="async" @error="onImgError" />
      <span v-if="verified" class="absolute bottom-[-3px] right-[-3px] flex items-center">
        <component :is="isAdmin ? Reputation : Verified" :style="{ width: `${width}px`, height: `${height}px` }" />
      </span>
    </component>
  </div>
</template>

<style scoped lang="scss">
.avatar {
  display: block;
  width: 40px;
  height: 40px;
  // border: 1px solid #71767b;
  position: relative;
  z-index: 1;
  border-radius: 100%;
  cursor: pointer;

  &-img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }

  &.lg {
    width: 66px;
    height: 66px;
  }

  &.xl {
    width: 96px;
    height: 96px;
  }

  &.md {
    width: 60px;
    height: 60px;
  }
}

.avatar-ring {
  position: relative;
  isolation: isolate;
  padding: 0;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  height: fit-content;
}

.avatar-ring::before {
  content: "";
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  background: var(--avatar-ring-background, transparent);
  z-index: 0;
}
</style>
